import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { includes } from 'ramda';
import { isNilOrEmptyString } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AccessControl from '@gef-ui/cdu-user/components/AccessControl';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import { resetForm } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage } from '@gef-ui/localization/messages';
import ChartLine from '@gef-ui/material-icons/ChartLine';
import CircleInfo from '@gef-ui/material-icons/CircleInfo';
import MagnifyingGlass from '@gef-ui/material-icons/MagnifyingGlass';
import Share from '@gef-ui/material-icons/Share';

import QuickFilter from 'components/atoms/GefMui/QuickFilter';
import ToolbarTemplate from 'components/atoms/GefMui/ToolbarTemplate/ToolbarTemplate';
import { ComponentLoader } from 'components/index';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { EMPTY_VALUES, USER_ROLES } from 'constants/general';
import { MPU_DISPLAY_TASK_STATISTIC, MPU_FORWARD_TASK, MPU_SEARCH_TASK } from 'features/common/acl/constants';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { subheaders } from 'features/common/Subheader/constants';
import { useCollapsible } from 'features/common/Subheader/hooks';
import { setVisibleState } from 'features/common/Subheader/slice';
import typeTasks from 'features/other/typeTasks';
import { clearAllSelected, clearSelectTasks } from 'features/other/typeTasks/actions';
import {
	changeQuickFilterView,
	fetchTaskTypeList,
	forwardTasks,
	proccessInfo,
} from 'features/views/taskOverview/actions';
import { formDataFieldsMap, QUICK_FILTER_STATES, quickFilterTypesSetup } from 'features/views/taskOverview/constants';
import { TASK_OVERVIEW_FORM_NAME } from 'features/views/taskOverview/form';
import { getQuickFilterState } from 'features/views/taskOverview/selectors';
import { resetCriteria } from 'features/views/taskSearch/actions';
import { getCurrentRole } from 'features/views/user/selectors';
import { getIsSubordinate } from 'features/views/userContext/selectors';
import { cleanse } from 'utils/object';
import { getRoutePath } from 'utils/routeHelper';

export const Toolbar = ({ taskTypesCodelist }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const next = useControlNext(TASK_OVERVIEW_FORM_NAME);
	const currentRole = useSelector(getCurrentRole);
	const { visible, handleToggle } = useCollapsible({ id: subheaders.tasksOverview });

	const quickFilterState = useSelector(getQuickFilterState);
	const isSubordinate = useSelector(getIsSubordinate);
	const hideStatisticsButton = useMemo(
		() => isNilOrEmptyString(currentRole) || currentRole === USER_ROLES.PPZ || currentRole === USER_ROLES.GUARANTOR,

		[currentRole],
	);

	const hasSelectedTasks = useSelector(typeTasks.selectors.hasSelectedTasks);
	const allSelected = useSelector(typeTasks.selectors.getMarkSelectedAll);
	const markAllSelected = includes(true, Object.values(allSelected));
	const checkIfProcessable = useSelector((state) => typeTasks.selectors.checkIfProcessable(state, taskTypesCodelist));

	const handleLink = useCallback(
		(link) => () => {
			if (link === 'taskSearch') {
				// resetting values when accessing TaskSearch page for the first time
				// to keep the prefillAndRefetch logic when going back from taskDetail to TaskSearch again
				dispatch(resetCriteria());
				dispatch(fetch.actions.clearFetchStore(fetch.constants.urls.taskList));
			}
			const { pathname, state } = getRoutePath(link, EMPTY_VALUES.OBJECT, true);
			navigate(pathname, { state });
		},
		[dispatch, navigate],
	);

	const handleTaskForward = useCallback(async () => {
		await dispatch(forwardTasks());
	}, [dispatch]);

	const handleProcessInformation = useCallback(async () => {
		await dispatch(proccessInfo());
	}, [dispatch]);

	const ignoreErrorsForProcessInfoTask = useIgnoreErrorsWhen({
		fetchStorePath: fetch.constants.urls.processTask,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 403,
	});

	const subheaderToolbar = (
		<Stack
			spacing={2}
			direction="row"
			sx={{
				justifyContent: 'flex-end',
			}}
		>
			{!isSubordinate && (
				<>
					<AccessControl accessLevel={MPU_SEARCH_TASK}>
						<Button
							variant="text"
							size="small"
							startIcon={<MagnifyingGlass />}
							onClick={handleLink('taskSearch')}
						>
							{getLabelMessage('sidebar:taskSearch')}
						</Button>
					</AccessControl>
					{!hideStatisticsButton && (
						<AccessControl accessLevel={MPU_DISPLAY_TASK_STATISTIC}>
							<Button
								variant="text"
								size="small"
								startIcon={<ChartLine />}
								onClick={handleLink('statistics')}
							>
								{getLabelMessage('sidebar:statistics')}
							</Button>
						</AccessControl>
					)}
				</>
			)}
			<AccessControl accessLevel={MPU_FORWARD_TASK}>
				<Button
					variant="text"
					size="small"
					startIcon={<Share />}
					onClick={handleTaskForward}
					disabled={markAllSelected ? !markAllSelected : !hasSelectedTasks}
				>
					{getLabelMessage('sidebar:submit')}
				</Button>
			</AccessControl>
			<ComponentLoader fetchStorePath={fetch.constants.urls.checkFinishAllProcessing} ignoreErrors>
				<ComponentLoader
					fetchStorePath={fetch.constants.urls.processTask}
					ignoreErrors={ignoreErrorsForProcessInfoTask}
				>
					<Button
						variant="text"
						size="small"
						startIcon={<CircleInfo />}
						onClick={handleProcessInformation}
						disabled={!checkIfProcessable}
					>
						{getLabelMessage('sidebar:processInformation')}
					</Button>
				</ComponentLoader>
			</ComponentLoader>
		</Stack>
	);

	const pushQuickFilterToDataLayer = useCallback((type) => {
		const formData = {};
		switch (type) {
			case QUICK_FILTER_STATES.all:
				formData.quickFilterAll = true;
				break;
			case QUICK_FILTER_STATES.tasks:
				formData.quickFilterTasks = true;
				break;
			case QUICK_FILTER_STATES.info:
				formData.quickFilterInfo = true;
				break;
			case QUICK_FILTER_STATES.processed:
				formData.quickFilterProcessed = true;
				break;
			default:
				formData.quickFilterAll = true;
				break;
		}

		pushToFilterDataLayer({
			filterSection: 'Úkoly a informace',
			formData: cleanse(formData),
			formDataFieldsMap,
		});
	}, []);

	const handleChangeQuickFilter = useCallback(
		(type) => {
			next(resetForm());
			dispatch(changeQuickFilterView(type));
			dispatch(clearSelectTasks());
			dispatch(clearAllSelected());
			dispatch(typeTasks.actions.setPersistTypeTasksVisibleState(true));

			if (type === QUICK_FILTER_STATES.processed) {
				dispatch(fetchTaskTypeList(type));
			} else if (quickFilterState === QUICK_FILTER_STATES.processed && type !== QUICK_FILTER_STATES.processed) {
				dispatch(fetchTaskTypeList());
			}

			if (visible) {
				// close mainFilter
				handleToggle();
			}

			pushQuickFilterToDataLayer(type);
		},
		[quickFilterState, pushQuickFilterToDataLayer, dispatch, handleToggle, visible, next],
	);

	const handleClickAlreadyChosenChipWhenCustomFilterIsVisible = useCallback(() => {
		// passed to onQuickFilterUnselect, but not really unselecting in this case
		// it handles case when clicking on a chip that is already chosen, but selection is hidden because custom filter is visible
		dispatch(setVisibleState({ id: subheaders.tasksOverview, visible: false }));
	}, [dispatch]);

	return (
		<ToolbarTemplate
			leftSide={
				<QuickFilter
					quickFilterState={quickFilterState}
					quickFilterTypesSetup={quickFilterTypesSetup}
					onChange={handleChangeQuickFilter}
					onReset={handleClickAlreadyChosenChipWhenCustomFilterIsVisible}
					subheaderOpen={visible}
				/>
			}
			rightSide={subheaderToolbar}
		/>
	);
};

Toolbar.propTypes = {
	taskTypesCodelist: PropTypes.array,
};

export default Toolbar;
